import { useContext, useEffect } from "react";
import { css } from "@emotion/css";
import { Box, CircularProgress } from "@mui/material";
import { PowerBIEmbed } from "powerbi-client-react";
import { Permissions, TokenType } from "powerbi-models"; // Adjust path as needed

import { apiHooks } from "~/api.js";
import { LoggedInUserContext } from "~/Contexts.js";

// Static class generated at module level — replaces the old makeStyles usage
const rootClass = css`
  height: 95%;
  width: 90%;
`;

export const NHSDashboardPage = () => {
  const userContext = useContext(LoggedInUserContext);
  const user = userContext?.user!;
  const [{ data: powerBiDashboardAccess, loading, error }] =
    apiHooks.reports.nhsDashboardData(
      user.sessionContext?.organisationId ?? "",
    );

  // Log error for debugging
  useEffect(() => {
    if (error) {
      console.error("Error fetching NHS dashboard data:", error);
    }
  }, [error]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error message if fetch fails
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        Error loading dashboard:{" "}
        {error.message || "Failed to fetch Power BI embed configuration"}
      </Box>
    );
  }

  // Render Power BI report if data is available
  return powerBiDashboardAccess ? (
    <Box
      sx={{
        height: "100vh",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <div style={{ height: "95%", width: "90%" }}>
        <PowerBIEmbed
          cssClassName={rootClass}
          embedConfig={{
            type: "report",
            id: powerBiDashboardAccess.dashboardId,
            permissions: Permissions.Read,
            embedUrl: powerBiDashboardAccess.embedUrl,
            accessToken: powerBiDashboardAccess.embedToken,
            tokenType: TokenType.Embed,
            pageView: "fitToWidth",
            settings: {
              panes: {
                filters: {
                  expanded: true,
                  visible: true,
                },
              },
            },
          }}
          eventHandlers={
            new Map([
              [
                "loaded",
                function () {
                  console.log("NHS Report loaded");
                },
              ],
              [
                "rendered",
                function () {
                  console.log("NHS Report rendered");
                },
              ],
              [
                "error",
                function (event: any) {
                  console.log("NHS Report error:", event?.detail);
                },
              ],
            ])
          }
          getEmbeddedComponent={(embeddedReport) => {
            // @ts-expect-error TODO(nhs-dashboard): the embedded report is
            // parked on window rather than held in state, and Window has no
            // such property.
            window.report = embeddedReport;
          }}
        />
      </div>
    </Box>
  ) : (
    <Box sx={{ p: 2 }}>No dashboard data available</Box>
  );
};
